import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import {
    copySequenceService,
    deleteSequenceStepService,
    getOperatorCompaniesService,
    getSequenceDueService,
    getSequenceService,
    getSequenceSummaryService,
    getSequencesService,
    saveSequenceService,
    saveSequenceStepService,
} from "../../../../Services/CRMAdminServices/CRMOperator";

/**
 * Follow-up sequences — Fintrack only.
 *
 * A sequence is a ladder of messages, SMS and email mixed, each with a day and
 * an hour after a lead goes on it. Two come ready built, Activation and
 * Reactivation, nine touches each. A client either runs ours or gets their own
 * copy to reword.
 *
 * The Outbox tab is the honest half: it shows what is due to go out right now
 * with the merge already done, and anything that cannot be sent says why in
 * plain English rather than going out with a gap in it.
 */

const CHANNELS = ["SMS", "Email"] as const;

const hourLabel = (h: number | null | undefined) => {
    if (h === null || h === undefined) return "as soon as due";
    const suffix = h < 12 ? "am" : "pm";
    const display = h % 12 === 0 ? 12 : h % 12;
    return `${display}${suffix}`;
};

const dayLabel = (d: number) => (d === 0 ? "Same day" : d === 1 ? "Next day" : `Day ${d}`);

const CRMSequences = () => {
    const [tab, setTab] = useState<"build" | "outbox">("build");

    const [companies, setCompanies] = useState<any[]>([]);
    const [companyId, setCompanyId] = useState<number | null>(null);

    const [sequences, setSequences] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const [openId, setOpenId] = useState<number | null>(null);
    const [detail, setDetail] = useState<any>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const [editing, setEditing] = useState<any | null>(null);
    const [saving, setSaving] = useState(false);

    const [due, setDue] = useState<any[]>([]);
    const [dueLoading, setDueLoading] = useState(false);
    const [summary, setSummary] = useState<any[]>([]);

    // ---------- loading ----------

    useEffect(() => {
        getOperatorCompaniesService({ isActive: true })
            .then((r: any) => {
                const list = r?.data ?? [];
                setCompanies(list);
                if (list.length > 0) setCompanyId(list[0].companyId);
            })
            .catch(() => toast.error("Could not load the client list."));
    }, []);

    const loadSequences = useCallback(async () => {
        if (!companyId) return;
        setLoading(true);
        try {
            const r: any = await getSequencesService({ companyId, includeTemplates: true });
            setSequences(r?.data ?? []);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not load the sequences.");
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => { loadSequences(); }, [loadSequences]);
    useEffect(() => { setOpenId(null); setDetail(null); setEditing(null); }, [companyId]);

    const loadOutbox = useCallback(async () => {
        setDueLoading(true);
        try {
            const [d, s]: any[] = await Promise.all([
                getSequenceDueService({ top: 200, companyId: companyId ?? undefined }),
                getSequenceSummaryService({ companyId: companyId ?? undefined }),
            ]);
            setDue(d?.data ?? []);
            setSummary(s?.data ?? []);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not load the outbox.");
        } finally {
            setDueLoading(false);
        }
    }, [companyId]);

    useEffect(() => { if (tab === "outbox") loadOutbox(); }, [tab, loadOutbox]);

    // ---------- actions ----------

    const openSequence = async (sequenceId: number) => {
        if (openId === sequenceId) { setOpenId(null); setDetail(null); setEditing(null); return; }
        setOpenId(sequenceId);
        setEditing(null);
        setDetailLoading(true);
        try {
            const r: any = await getSequenceService(sequenceId);
            setDetail(r?.data ?? null);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not load that sequence.");
        } finally {
            setDetailLoading(false);
        }
    };

    const copyToClient = async (sequenceId: number, name: string) => {
        if (!companyId) return;
        try {
            const r: any = await copySequenceService({ sequenceId, companyId, name: `${name} — ${clientName}` });
            toast.success("Copied. Edit away — it is theirs now.");
            await loadSequences();
            if (r?.data?.sequenceId) await openSequence(r.data.sequenceId);
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not copy that sequence.");
        }
    };

    const toggleActive = async (s: any) => {
        try {
            await saveSequenceService({
                sequenceId: s.sequenceId,
                companyId: s.companyId,
                name: s.name,
                campaign: s.campaign,
                description: s.description,
                isActive: !s.isActive,
            });
            await loadSequences();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not change that.");
        }
    };

    const saveStep = async () => {
        if (!editing) return;
        if (!String(editing.body || "").trim()) { toast.error("The message is empty."); return; }
        if (editing.channel === "Email" && !String(editing.subject || "").trim()) {
            toast.error("An email needs a subject line."); return;
        }
        setSaving(true);
        try {
            await saveSequenceStepService({
                stepId: editing.stepId || 0,
                sequenceId: editing.sequenceId,
                stepNo: Number(editing.stepNo) || 1,
                dayOffset: Number(editing.dayOffset) || 0,
                sendHour: editing.sendHour === "" || editing.sendHour === null ? null : Number(editing.sendHour),
                channel: editing.channel,
                subject: editing.channel === "Email" ? editing.subject : null,
                body: editing.body,
                isActive: editing.isActive !== false,
            });
            toast.success("Saved.");
            setEditing(null);
            const r: any = await getSequenceService(editing.sequenceId);
            setDetail(r?.data ?? null);
            await loadSequences();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not save the step.");
        } finally {
            setSaving(false);
        }
    };

    const removeStep = async (step: any) => {
        try {
            await deleteSequenceStepService(step.stepId, step.sequenceId);
            const r: any = await getSequenceService(step.sequenceId);
            setDetail(r?.data ?? null);
            await loadSequences();
        } catch (e: any) {
            toast.error(e?.response?.data?.message || "Could not remove that step.");
        }
    };

    const addStep = (sequenceId: number) => {
        const steps = detail?.steps ?? [];
        const last = steps.length > 0 ? steps[steps.length - 1] : null;
        setEditing({
            stepId: 0,
            sequenceId,
            stepNo: (last?.stepNo ?? 0) + 1,
            dayOffset: (last?.dayOffset ?? 0) + 3,
            sendHour: 10,
            channel: "SMS",
            subject: "",
            body: "",
            isActive: true,
        });
    };

    const clientName = useMemo(
        () => companies.find((c: any) => c.companyId === companyId)?.businessName ?? "",
        [companies, companyId],
    );

    const blocked = due.filter((d: any) => !d.canSend);
    const ready = due.filter((d: any) => d.canSend);

    return (
        <div className="p-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h3 className="f-w-900 mb-0" style={{ color: "blue" }}>Follow-up sequences</h3>
                <div className="d-flex align-items-center gap-2">
                    <select className="form-select form-select-sm" style={{ width: 240 }}
                        value={companyId ?? ""} onChange={(e) => setCompanyId(Number(e.target.value))}>
                        {companies.map((c: any) => (
                            <option key={c.companyId} value={c.companyId}>{c.businessName}</option>
                        ))}
                    </select>
                    <Button color={tab === "build" ? "primary" : "secondary"} size="sm" outline={tab !== "build"}
                        onClick={() => setTab("build")}>Sequences</Button>
                    <Button color={tab === "outbox" ? "primary" : "secondary"} size="sm" outline={tab !== "outbox"}
                        onClick={() => setTab("outbox")}>Outbox</Button>
                </div>
            </div>

            {tab === "build" ? (
                <div className="p-3" style={panel}>
                    {loading ? (
                        <div className="text-center py-4"><Spinner size="sm" /></div>
                    ) : sequences.length === 0 ? (
                        <div className="text-muted py-3">No sequences yet.</div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-sm align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: "30%" }}>Sequence</th>
                                        <th>Campaign</th>
                                        <th className="text-end">Steps</th>
                                        <th className="text-end">SMS</th>
                                        <th className="text-end">Email</th>
                                        <th className="text-end">Runs over</th>
                                        <th className="text-end">On it now</th>
                                        <th style={{ width: 180 }} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {sequences.map((s: any) => (
                                        <Fragment key={s.sequenceId}>
                                            <tr style={{ cursor: "pointer" }} onClick={() => openSequence(s.sequenceId)}>
                                                <td className="f-w-600">
                                                    {s.name}
                                                    {s.isTemplate && <Badge color="info" className="ms-2">Fintrack</Badge>}
                                                    {!s.isActive && <Badge color="light" className="ms-2 text-dark">Off</Badge>}
                                                </td>
                                                <td>{s.campaign}</td>
                                                <td className="text-end f-w-600">{s.steps}</td>
                                                <td className="text-end">{s.smsSteps}</td>
                                                <td className="text-end">{s.mailSteps}</td>
                                                <td className="text-end">{s.runsOver} days</td>
                                                <td className="text-end">{s.active}</td>
                                                <td className="text-end" onClick={(e) => e.stopPropagation()}>
                                                    {s.isTemplate ? (
                                                        <Button color="link" size="sm" className="p-0"
                                                            onClick={() => copyToClient(s.sequenceId, s.name)}>
                                                            Copy for this client
                                                        </Button>
                                                    ) : (
                                                        <Button color="link" size="sm" className="p-0"
                                                            onClick={() => toggleActive(s)}>
                                                            {s.isActive ? "Switch off" : "Switch on"}
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>

                                            {openId === s.sequenceId && (
                                                <tr>
                                                    <td colSpan={8} style={{ background: "#fafbff" }}>
                                                        {detailLoading ? (
                                                            <div className="text-center py-3"><Spinner size="sm" /></div>
                                                        ) : (
                                                            <div className="p-2">
                                                                {(detail?.steps ?? []).map((step: any) => (
                                                                    <div key={step.stepId} className="mb-2 p-2" style={stepBox}>
                                                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                                                            <div>
                                                                                <Badge color={step.channel === "Email" ? "secondary" : "primary"}>
                                                                                    {step.channel}
                                                                                </Badge>
                                                                                <span className="ms-2 f-w-600" style={{ fontSize: 13 }}>
                                                                                    Step {step.stepNo} — {dayLabel(step.dayOffset)}, {hourLabel(step.sendHour)}
                                                                                </span>
                                                                            </div>
                                                                            {!s.isTemplate && (
                                                                                <div className="d-flex gap-2">
                                                                                    <Button color="link" size="sm" className="p-0"
                                                                                        onClick={() => setEditing({ ...step })}>Edit</Button>
                                                                                    <Button color="link" size="sm" className="p-0 text-danger"
                                                                                        onClick={() => removeStep(step)}>Remove</Button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        {step.subject && (
                                                                            <div className="f-w-600" style={{ fontSize: 13 }}>{step.subject}</div>
                                                                        )}
                                                                        <div style={{ fontSize: 13, whiteSpace: "pre-wrap", color: "#444" }}>
                                                                            {step.body}
                                                                        </div>
                                                                    </div>
                                                                ))}

                                                                {s.isTemplate ? (
                                                                    <small className="text-muted">
                                                                        This is a Fintrack sequence. Copy it for the client before editing.
                                                                    </small>
                                                                ) : editing?.sequenceId === s.sequenceId ? (
                                                                    <div className="p-3" style={stepBox}>
                                                                        <div className="row g-2 mb-2">
                                                                            <div className="col-6 col-md-2">
                                                                                <label className="form-label mb-1" style={{ fontSize: 12 }}>Step</label>
                                                                                <input type="number" className="form-control form-control-sm"
                                                                                    value={editing.stepNo}
                                                                                    onChange={(e) => setEditing({ ...editing, stepNo: e.target.value })} />
                                                                            </div>
                                                                            <div className="col-6 col-md-2">
                                                                                <label className="form-label mb-1" style={{ fontSize: 12 }}>Day</label>
                                                                                <input type="number" className="form-control form-control-sm"
                                                                                    value={editing.dayOffset}
                                                                                    onChange={(e) => setEditing({ ...editing, dayOffset: e.target.value })} />
                                                                            </div>
                                                                            <div className="col-6 col-md-2">
                                                                                <label className="form-label mb-1" style={{ fontSize: 12 }}>Hour (0–23)</label>
                                                                                <input type="number" className="form-control form-control-sm"
                                                                                    value={editing.sendHour ?? ""}
                                                                                    onChange={(e) => setEditing({ ...editing, sendHour: e.target.value })} />
                                                                            </div>
                                                                            <div className="col-6 col-md-2">
                                                                                <label className="form-label mb-1" style={{ fontSize: 12 }}>Channel</label>
                                                                                <select className="form-select form-select-sm"
                                                                                    value={editing.channel}
                                                                                    onChange={(e) => setEditing({ ...editing, channel: e.target.value })}>
                                                                                    {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                                                                                </select>
                                                                            </div>
                                                                            {editing.channel === "Email" && (
                                                                                <div className="col-12 col-md-4">
                                                                                    <label className="form-label mb-1" style={{ fontSize: 12 }}>Subject</label>
                                                                                    <input className="form-control form-control-sm"
                                                                                        value={editing.subject ?? ""}
                                                                                        onChange={(e) => setEditing({ ...editing, subject: e.target.value })} />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <textarea className="form-control form-control-sm mb-2"
                                                                            rows={editing.channel === "Email" ? 8 : 4}
                                                                            value={editing.body ?? ""}
                                                                            onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
                                                                        <div className="d-flex justify-content-between align-items-center">
                                                                            <small className="text-muted">
                                                                                Merge fields: {"{FirstName} {Surname} {BusinessName} {BrokerName} {BookingLink}"}
                                                                            </small>
                                                                            <div className="d-flex gap-2">
                                                                                <Button color="primary" size="sm" disabled={saving} onClick={saveStep}>
                                                                                    {saving ? "Saving…" : "Save step"}
                                                                                </Button>
                                                                                <Button color="link" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <Button color="secondary" size="sm" outline
                                                                        onClick={() => addStep(s.sequenceId)}>Add a step</Button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            ) : (
                <>
                    <div className="d-flex mb-3" style={{ gap: 12, flexWrap: "wrap" }}>
                        <Tile label="Ready to send" value={ready.length} strong />
                        <Tile label="Held back" value={blocked.length} />
                        <Tile label="On a sequence" value={summary.reduce((a: number, s: any) => a + (s.onSequence ?? 0), 0)} />
                        <Tile label="Replied" value={summary.reduce((a: number, s: any) => a + (s.replied ?? 0), 0)} />
                        <Tile label="Booked" value={summary.reduce((a: number, s: any) => a + (s.booked ?? 0), 0)} strong />
                        <Tile label="Opted out" value={summary.reduce((a: number, s: any) => a + (s.optedOut ?? 0), 0)} />
                    </div>

                    <div className="p-3" style={panel}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="f-w-700 mb-0">Due to go out now</h6>
                            <Button color="secondary" size="sm" outline onClick={loadOutbox}>Refresh</Button>
                        </div>

                        {dueLoading ? (
                            <div className="text-center py-4"><Spinner size="sm" /></div>
                        ) : due.length === 0 ? (
                            <div className="text-muted py-3">
                                Nothing due. Either no leads are on a sequence yet, or everyone is waiting on their next day.
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-sm align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th style={{ width: 70 }}>Ch</th>
                                            <th style={{ width: "18%" }}>To</th>
                                            <th style={{ width: 60 }} className="text-end">Step</th>
                                            <th>Message</th>
                                            <th style={{ width: "18%" }}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {due.map((d: any) => (
                                            <tr key={d.enrolmentId} style={d.canSend ? undefined : { background: "#fff8f6" }}>
                                                <td>
                                                    <Badge color={d.channel === "Email" ? "secondary" : "primary"}>{d.channel}</Badge>
                                                </td>
                                                <td>
                                                    <div className="f-w-600" style={{ fontSize: 13 }}>
                                                        {[d.firstName, d.surname].filter(Boolean).join(" ") || "—"}
                                                    </div>
                                                    <div className="text-muted" style={{ fontSize: 12 }}>{d.recipient || "no contact"}</div>
                                                </td>
                                                <td className="text-end">{d.nextStep}</td>
                                                <td>
                                                    {d.subject && <div className="f-w-600" style={{ fontSize: 13 }}>{d.subject}</div>}
                                                    <div style={{ fontSize: 13, color: "#444", whiteSpace: "pre-wrap" }}>{d.body}</div>
                                                </td>
                                                <td>
                                                    {d.canSend
                                                        ? <Badge color="success">Ready</Badge>
                                                        : <span className="text-danger" style={{ fontSize: 12.5 }}>{d.cannotSendReason}</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <small className="text-muted d-block mt-2">
                            Nothing here has been sent. A message with a gap in it is held back on purpose — it never goes out half-filled.
                        </small>
                    </div>
                </>
            )}
        </div>
    );
};

const panel = {
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#fff",
} as const;

const stepBox = {
    borderRadius: 8,
    border: "1px solid #e6e8f0",
    background: "#fff",
} as const;

const Tile = ({ label, value, strong }: { label: string; value: any; strong?: boolean }) => (
    <div style={{
        border: "1px solid #e6e8f0", borderRadius: 8, padding: "10px 16px",
        background: strong ? "#f0f9f2" : "#fff", minWidth: 120,
    }}>
        <div style={{ fontSize: 11.5, color: "#767d95", fontWeight: 600, letterSpacing: 0.3 }}>{label}</div>
        <div style={{ fontSize: 19, fontWeight: 800, color: strong ? "#1a7f37" : "#111528" }}>{value}</div>
    </div>
);

export default CRMSequences;
